import {
  Box,
  mdBumps,
  ModalHeader,
  SectionProps,
  Typo,
  useModal,
} from '@wrisk/ui-components'
import React, { FunctionComponent, useCallback } from 'react'

import { CoverOption, getCurrentQuote } from '../../../domain'
import {
  useProposalAdjustment,
  useProposalFormData,
} from '../../../hooks/adjustments/proposal'
import {
  TExtends,
  TKey,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { FormConfig, getCoreAssetType } from '../../../state/configuration'
import { FormDisabled } from '../../formBuilder'
import { useProduct } from '../../product/productContext'
import { useProposal } from '../../product/proposal/ProposalProvider'
import { CoverSection } from '../covers'

export const ProposalCoverSection: FunctionComponent<SectionProps> = (props) => {
  const { proposal } = useProposal()
  const { product } = useProduct()

  const { covers } = getCoreAssetType(product)

  const modal = useModal()

  const quote = getCurrentQuote(proposal)

  const onChange = useCallback(
    (coverOption: CoverOption) => {
      const { form } =
        covers.optional?.find((it) => it.code === coverOption.coverOption) ?? {}

      if (!form) {
        throw new Error(`No form found for optional cover ${coverOption.coverOption}`)
      }

      modal.show({
        content: <ProposalEditOptionalCoverModal form={form} onComplete={modal.hide} />,
      })
    },
    [covers, modal],
  )

  return <CoverSection quote={quote} onChange={onChange} {...props} />
}

const tKey = TKey('components.edit-optional-cover-modal')

interface EditOptionalCoverModalProps {
  form: FormConfig
  onComplete: () => void
}

const ProposalEditOptionalCoverModal: FunctionComponent<EditOptionalCoverModalProps> = ({
  form,
  onComplete,
}) => {
  const { t } = useWriskTranslation()

  const data = useProposalFormData(form)
  const onSubmitAdjustment = useProposalAdjustment({
    form: form,
    applyRating: true,
    onComplete: onComplete,
  })

  return (
    <Box>
      <ModalHeader header={t(tKey('header'))} />
      <Box marginBottom={mdBumps}>
        <Typo>{t(tKey('description'))}</Typo>
      </Box>
      <FormDisabled
        id='editOptionalCover'
        data={data}
        config={form}
        onSubmit={onSubmitAdjustment.execute}
        loading={onSubmitAdjustment.loading}
        tKey={TExtends(tKey, 'inputs')}
        t={t}
      />
    </Box>
  )
}
